/**
 * Pieces the Friends screen and the friend link screen share (direct friends, ticket 03): the list of what
 * friends will see, and the card that shows a link just made (a circle's code card and a friend link are the same
 * card). Templates and two small clipboard helpers; no state of their own.
 */
import { html, nothing, type TemplateResult } from 'lit';
import { t } from '../i18n';
import { tPart } from '../bidi-html';
import { nameOf } from './raid/raid-bits';

/** A typed code as the server reads it (case, spaces, dashes and look-alike letters do not matter): what goes in a friend link's path. */
export const normaliseCode = (raw: string) => raw.toUpperCase().replace(/[\s-]+/g, '').replace(/O/g, '0').replace(/[IL]/g, '1');

/** Which button of a link card has just copied: each shows its own "copied" and nothing else's. */
export type Copied = '' | 'share' | 'link' | 'code';

/** A plain-language summary and expandable full list, shared by circle and direct-friend consent. */
export function consentList(): TemplateResult {
  return html`
    <div class="sharing-summary">
      <p>${t('circle.sharingSummary')}</p>
      <p class="muted small">${t('circle.sharingSpoils')}</p>
      <p class="muted small">${t('circle.sharingPrivate')}</p>
    </div>
    <details class="sharing-details">
      <summary>${t('circle.sharingDetails')}</summary>
      <div class="sharing-detail-content">
        <p><strong>${t('circle.seeTitle')}</strong></p>
        <ul class="circle-see">
          <li>${t('circle.seeTown')}</li>
          <li>${t('circle.seeGlory')}</li>
          <li>${t('circle.seeDistricts')}</li>
          <li>${t('circle.seeName')}</li>
          <li>${t('circle.seeRaid')}</li>
          <li>${t('circle.seeSpoils')}</li>
        </ul>
        <p><strong>${t('circle.neverTitle')}</strong></p>
        <ul class="circle-never">
          <li>${t('circle.neverWords')}</li>
          <li>${t('circle.neverTitles')}</li>
          <li>${t('circle.neverRecordings')}</li>
        </ul>
      </div>
    </details>`;
}

/** Who the learner has just become friends with: the Friends screen says so once, then forgets (never kept on disk). */
let newFriend = '';
export const noteNewFriend = (name: string) => { newFriend = name; };
export function takeNewFriend(): string {
  const n = newFriend;
  newFriend = '';
  return n;
}

/** Copy to the clipboard; false when this browser will not (the link and code stay on screen to copy by hand). */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** The share sheet when there is one, else the link is copied. `none`: the learner closed the sheet. `failed`: nothing worked. */
export async function shareLink(url: string): Promise<'shared' | 'copied' | 'none' | 'failed'> {
  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ url });
      return 'shared';
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return 'none'; // the learner closed the sheet
    }
  }
  return (await copyText(url)) ? 'copied' : 'failed';
}

export interface LinkCard {
  /** The link leads and the code sits under the buttons (the friend link card, which also takes the class `friend-link-card`). */
  linkFirst?: boolean;
  title: string;
  hint: string;
  /** The code to read out or type, in the grouped form: a circle's, or a friend link's secret. */
  code: string;
  copied: Copied;
  /** Set when a share or copy did not work (a plain-HTTP page has no clipboard): the link itself, to copy by hand. */
  byHand?: string;
  onShare: () => void;
  onCopyLink: () => void;
  onCopyCode: () => void;
  /** A line under the buttons (this link is no longer open). */
  note?: string;
}

/** Select the whole field, so one tap and the phone's own Copy is all that is left. */
const selectAll = (e: Event) => (e.target as HTMLInputElement).select();

/**
 * The card for a code or link shown once: Share the link (main), Copy the link, and the code. A circle's code leads;
 * on `linkFirst` (a friend link) the link comes first and the code sits under the buttons.
 */
export function linkCard(c: LinkCard): TemplateResult {
  const code = html`<p class="circle-code" dir="ltr">${c.code}</p>
    <p class="circle-copy-row"><button class="btn ghost circle-copy" @click=${c.onCopyCode}>${c.copied === 'code' ? t('circle.copied') : t('circle.copy')}</button></p>`;
  return html`<div class="card circle-code-card ${c.linkFirst ? 'friend-link-card' : ''}">
    <h3 tabindex="-1">${c.title}</h3>
    ${c.linkFirst ? nothing : code}
    <p class="muted small">${c.hint}</p>
    <button class="btn primary block circle-share" @click=${c.onShare}>${c.copied === 'share' ? t('circle.linkCopied') : t('circle.share')}</button>
    <button class="btn block circle-copy-link" style="margin-top:8px" @click=${c.onCopyLink}>${c.copied === 'link' ? t('circle.linkCopied') : t('circle.copyLink')}</button>
    ${c.byHand
      ? html`<p class="muted small link-by-hand-hint">${t('friends.copyByHand')}</p>
          <input class="circle-code-input link-by-hand" readonly dir="ltr" .value=${c.byHand} aria-label=${t('friends.copyByHand')}
            @focus=${selectAll} @click=${selectAll} />`
      : nothing}
    ${c.linkFirst ? code : nothing}
    ${c.note ? html`<p class="muted small friend-link-note" role="status">${c.note}</p>` : nothing}
  </div>`;
}

export interface FriendAsk {
  name: string;
  busy: boolean;
  /** A refusal to show inside the card, above the buttons. */
  error?: string;
  onAgree: () => void;
  onCancel: () => void;
}

/** "*name* wants to be friends": what each of you will see, **I agree — be friends** and **Not now**. A link and a request share it. */
export function friendAskCard(a: FriendAsk): TemplateResult {
  return html`
    <div class="card friend-ask">
      <h3 tabindex="-1">${tPart('friends.wants', 'name', nameOf(a.name))}</h3>
      ${consentList()}
      <p class="muted small friend-end-any">${t('friends.endAnyTime')}</p>
      <p class="muted small">${t('friends.bothWays')}</p>
      ${a.error ? html`<p class="circle-error" role="alert">${a.error}</p>` : nothing}
      <button class="btn primary block friend-agree" ?disabled=${a.busy} @click=${a.onAgree}>${t('friends.agreeBe')}</button>
      <button class="btn ghost block friend-cancel" style="margin-top:8px" ?disabled=${a.busy} @click=${a.onCancel}>${t('circle.cancel')}</button>
    </div>
  `;
}
